/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import {
    Accordion, AccordionSummary, AccordionDetails,
    Box, Typography, Stack, alpha,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { common } from 'src/theme/palette';
import FormSelector from './FormSelector';
import PerFormBindingList from './PerFormBindingList';

/* ──────────────────────────────────────────────────────────────────────────
   AdvancedReveal — multi-form sequence builder
   ────────────────────────────────────────────
   Accordion that sits directly under the Forms section in the wizard.

   Naming history (May 2026): the summary text previously said
   "Advanced — build a multi-form sequence", which read awkwardly inside
   the Advanced view itself ("Advanced inside Advanced"). The label is
   now just "Multi-form sequence". When the parent wizard is itself in
   Advanced mode, the accordion is default-expanded so the sequence
   builder is the primary surface, not a hidden affordance.

   Open this to:
     - pick multiple forms via search/filter/preset
     - reorder them by drag
     - bind per-form treatment / treated-by overrides
     - save the current selection as a preset
   ────────────────────────────────────────────────────────────────────────── */

const AdvancedReveal = ({
    forms,
    formsLoading,
    selectedForms,
    onAddForm,
    onRemoveForm,
    onReorderForms,
    practiceId,
    sequencePresets,
    onApplyPreset,
    onManagePresets,
    onSaveCurrentAsPreset,
    defaultExpanded,
    // Per-form binding overrides (treatment / treated-by) — wired by the
    // parent dialog. Optional — when omitted the binding panel still
    // renders but the override map starts empty and stays empty.
    treatmentOptions,
    practitionerOptions,
    perFormTreatmentIds,
    onPerFormTreatmentChange,
    perFormPracticeFieldValues,
    onPerFormPracticeFieldChange,
}) => (
    <Accordion
        defaultExpanded={defaultExpanded}
        elevation={0}
        sx={{
            border: '1px solid',
            borderColor: alpha('#919EAB', 0.16),
            borderRadius: '10px !important',
            overflow: 'hidden',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
        }}
    >
        <AccordionSummary
            expandIcon={<Iconify icon="eva:chevron-down-fill" width={18} />}
            sx={{
                bgcolor: '#F9FAFB',
                minHeight: 44,
                '&.Mui-expanded': { minHeight: 44 },
                '& .MuiAccordionSummary-content': {
                    my: '10px',
                    '&.Mui-expanded': { my: '10px' },
                },
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify
                    icon="solar:playlist-minimalistic-bold-duotone"
                    width={15}
                    style={{ color: common.mintyZest }}
                />
                <Stack direction="column" sx={{ gap: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#454F5B', lineHeight: 1.25 }}>
                        Multi-form sequence
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#637381', lineHeight: 1.25 }}>
                        Send more than one form in a single link. Drag to reorder, bind per-form context.
                    </Typography>
                </Stack>
            </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: '#fff', p: 2 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.5,
                    minHeight: 280,
                }}
            >
                {/* Available — same filter/search/preset UI as the
                    compact picker, but multi-select. */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '10.5px', fontWeight: 700, color: '#919EAB',
                            textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5,
                        }}
                    >
                        Available
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <FormSelector
                            forms={forms}
                            formsLoading={formsLoading}
                            selectedForms={selectedForms}
                            onAdd={onAddForm}
                            onRemove={onRemoveForm}
                            practiceId={practiceId}
                            sequencePresets={sequencePresets}
                            onApplyPreset={onApplyPreset}
                            onManagePresets={onManagePresets}
                            onSaveCurrentAsPreset={onSaveCurrentAsPreset}
                        />
                    </Box>
                </Box>

                {/* Selected — drag-to-reorder list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '10.5px', fontWeight: 700, color: '#919EAB',
                            textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5,
                        }}
                    >
                        Sequence ({selectedForms.length})
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <PerFormBindingList
                            selectedForms={selectedForms}
                            onRemove={onRemoveForm}
                            onReorder={onReorderForms}
                            practiceId={practiceId}
                            treatmentOptions={treatmentOptions}
                            practitionerOptions={practitionerOptions}
                            perFormTreatmentIds={perFormTreatmentIds}
                            onPerFormTreatmentChange={onPerFormTreatmentChange}
                            perFormPracticeFieldValues={perFormPracticeFieldValues}
                            onPerFormPracticeFieldChange={onPerFormPracticeFieldChange}
                        />
                    </Box>
                </Box>
            </Box>
        </AccordionDetails>
    </Accordion>
);

AdvancedReveal.propTypes = {
    forms: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    formsLoading: PropTypes.bool,
    selectedForms: PropTypes.array.isRequired,
    onAddForm: PropTypes.func.isRequired,
    onRemoveForm: PropTypes.func.isRequired,
    onReorderForms: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sequencePresets: PropTypes.array,
    onApplyPreset: PropTypes.func,
    onManagePresets: PropTypes.func,
    onSaveCurrentAsPreset: PropTypes.func,
    defaultExpanded: PropTypes.bool,
    treatmentOptions: PropTypes.array,
    practitionerOptions: PropTypes.array,
    perFormTreatmentIds: PropTypes.object,
    onPerFormTreatmentChange: PropTypes.func,
    perFormPracticeFieldValues: PropTypes.object,
    onPerFormPracticeFieldChange: PropTypes.func,
};

AdvancedReveal.defaultProps = {
    forms: null,
    formsLoading: false,
    sequencePresets: [],
    onApplyPreset: () => {},
    onManagePresets: () => {},
    onSaveCurrentAsPreset: () => {},
    defaultExpanded: false,
    treatmentOptions: [],
    practitionerOptions: [],
    perFormTreatmentIds: {},
    onPerFormTreatmentChange: () => {},
    perFormPracticeFieldValues: {},
    onPerFormPracticeFieldChange: () => {},
};

export default AdvancedReveal;
